import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import fluidPlayer from 'fluid-player';
import { 
  getVideoShowById, 
  getVideoStreamUrl, 
  getThumbnailStreamUrl, 
  incrementVideoView,
  rateVideo, 
  removeRating, 
  getMyRating
} from '../../../http/Actions/VideoShowAction';
import { FaThumbsUp, FaThumbsDown, FaShare, FaDownload, FaEllipsisH, FaEye, FaRegCalendarAlt, FaCheck } from 'react-icons/fa';
import SugeridosDestacados from './Sugeridos';
import sinFoto from '../../../../images/sin-foto.png';

const ReproductorVideoShows = () => {
  const { idCapitulo } = useParams();
  const navigate = useNavigate();
  const [capitulo, setCapitulo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [totalWatchedTime, setTotalWatchedTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const lastUpdateTimeRef = useRef(null);
  const playerInitializedRef = useRef(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [viewIncremented, setViewIncremented] = useState(false);
  const [userRating, setUserRating] = useState(null);
  const [totalLikes, setTotalLikes] = useState(0);
  const [ratingLoading, setRatingLoading] = useState(false);
  let contador = 0;

  // Función para copiar al portapapeles
  const handleShare = () => {
    const currentURL = window.location.href;
    
    navigator.clipboard.writeText(currentURL)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => {
          setCopySuccess(false);
        }, 2000);
      })
      .catch(err => {
        console.error('Error al copiar el enlace: ', err);
      });
  };

  // Función para ir al perfil del comediante
  const handleComedianteClick = (comedianteId) => {
    if (comedianteId) {
      navigate(`/comediante/${comedianteId}`);
    }
  };

  // Función para manejar Like
  const handleLike = async () => {
    if (ratingLoading) return;
    
    const token = localStorage.getItem('token');
    setRatingLoading(true);

    try {
      if (userRating === true) {
        const response = await removeRating(idCapitulo, token);
        setUserRating(null);
        setTotalLikes(response.rating.totalLikes);
      } else {
        const response = await rateVideo(idCapitulo, true, null, token);
        setUserRating(true);
        setTotalLikes(response.rating.totalLikes);
      }
    } catch (error) {
      console.error('Error al dar like:', error);
    } finally {
      setRatingLoading(false);
    }
  };

  // Función para manejar Dislike
  const handleDislike = async () => {
    if (ratingLoading) return;
    
    const token = localStorage.getItem('token');
    setRatingLoading(true);

    try {
      if (userRating === false) {
        const response = await removeRating(idCapitulo, token);
        setUserRating(null);
        setTotalLikes(response.rating.totalLikes);
      } else {
        const response = await rateVideo(idCapitulo, false, null, token);
        setUserRating(false);
        setTotalLikes(response.rating.totalLikes);
      }
    } catch (error) {
      console.error('Error al dar dislike:', error);
    } finally {
      setRatingLoading(false);
    }
  };

  // Cargar información del video
  useEffect(() => {
    if(contador === 0) {
      const token = localStorage.getItem('token');
      setLoading(true);
      
      getVideoShowById(
        idCapitulo,
        (response) => {
          if (response.status === 200 && response.data) {
            setCapitulo(response.data);
          } else {
            setError("Formato de respuesta inesperado");
          }
          setLoading(false);
        },
        (err) => {
          setError(err.message || "Error al cargar el capítulo");
          setLoading(false);
        }, 
        token
      );
    }
    contador++;

    // Limpieza al desmontar
    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.error("Error al destruir el reproductor:", e);
        }
        playerRef.current = null;
      }
      playerInitializedRef.current = false;
    };
  }, [idCapitulo, contador]);

  // Cargar calificación del usuario
  useEffect(() => {
    if (capitulo) {
      const token = localStorage.getItem('token');
      
      getMyRating(idCapitulo, token)
        .then((response) => {
          if (response.hasRated) {
            setUserRating(response.rating.isLike);
          }
        })
        .catch((error) => {
        });

      setTotalLikes(capitulo.totalLikes  || 0);
    }
  }, [capitulo, idCapitulo]);

  // Cargar URLs del video y thumbnail cuando tengamos el capítulo
  useEffect(() => {
    if (capitulo && !videoUrl) {
      const token = localStorage.getItem('token');
      setLoadingVideo(true);

      // Cargar ambas URLs en paralelo
      Promise.all([
        getVideoStreamUrl(idCapitulo, token),
        getThumbnailStreamUrl(idCapitulo, token).catch((error) => {
          console.warn('Error obteniendo thumbnail (opcional):', error);
          return null; // Thumbnail es opcional
        })
      ])
      .then(([videoResponse, thumbnailResponse]) => {
        setVideoUrl(videoResponse.videoUrl);
        if (thumbnailResponse?.thumbnailUrl) {
          setThumbnailUrl(thumbnailResponse.thumbnailUrl);
        }
        setLoadingVideo(false);
      })
      .catch((error) => {
        console.error('Error obteniendo URLs de stream:', error);
        setError(`Error al cargar el video: ${error}`);
        setLoadingVideo(false);
      });
    }
  }, [capitulo, idCapitulo, videoUrl]);

  // Inicializar reproductor cuando tengamos la URL del video
  useEffect(() => {
    if (videoRef.current && videoUrl && !playerInitializedRef.current && !loadingVideo) {
      const videoElement = document.getElementById('video-id');
      if (!videoElement) return;

      try {
        const initTimeout = setTimeout(() => {
          if (!playerRef.current && videoElement && videoUrl) {
            playerInitializedRef.current = true;

            // Inicializar Fluid Player
            playerRef.current = fluidPlayer('video-id', {
              layoutControls: {
                subtitlesEnabled: true,
                subtitlesOnByDefault: false,
                primaryColor: "#6D28D9",
                controlBar: {
                  autoHideTimeout: 3,
                  animated: true,
                  autoHide: true
                },
                autoPlay: false,
                mute: false,
                allowTheatre: false,
                playPauseAnimation: true,
                playbackRateEnabled: true,
                allowDownload: false,
                playButtonShowing: true,
                fillToContainer: true,
                posterImage: thumbnailUrl || ''
              }
            });

            const handleFirstPlay = () => {
              if (!viewIncremented) {
                const token = localStorage.getItem('token');

                incrementVideoView(idCapitulo, token)
                  .then((response) => {
                    setViewIncremented(true);
                  })
                  .catch((error) => {
                    console.error('Error incrementando vista:', error);
                  });
              }
            };

            videoElement.addEventListener('play', handleFirstPlay, { once: true });

            const updateVideoDuration = () => {
              if (videoElement && videoElement.duration && !isNaN(videoElement.duration)) {
                setVideoDuration(videoElement.duration);
              }
            };

            // 🔸 Forzar lectura inmediata si ya está cargada
            if (videoElement.readyState >= 1 && videoElement.duration && !isNaN(videoElement.duration)) {
              updateVideoDuration();
            }


            const resetWatchedTime = () => {
              setTotalWatchedTime(0);
              lastUpdateTimeRef.current = null;
            };

            const updateWatchedTime = () => {
              const currentTime = videoElement.currentTime;
              if (currentTime < 0.1) {
                resetWatchedTime();
              } else if (lastUpdateTimeRef.current !== null) {
                const timeDiff = currentTime - lastUpdateTimeRef.current;
                if (timeDiff > 0 && timeDiff < 1) {
                  setTotalWatchedTime(prev => prev + timeDiff);
                }
              }
              lastUpdateTimeRef.current = currentTime;
            };

            videoElement.addEventListener('loadedmetadata', updateVideoDuration);
            videoElement.addEventListener('timeupdate', updateWatchedTime);
            videoElement.addEventListener('seeking', () => {
              if (videoElement.currentTime < 0.1) resetWatchedTime();
              else lastUpdateTimeRef.current = null;
            });

            return () => {
              videoElement.removeEventListener('play', handleFirstPlay);
              videoElement.removeEventListener('loadedmetadata', updateVideoDuration);
              videoElement.removeEventListener('timeupdate', updateWatchedTime);
              videoElement.removeEventListener('seeking', resetWatchedTime);
            };
          }
        }, 500);

        return () => clearTimeout(initTimeout);
      } catch (error) {
        console.error("Error al inicializar el reproductor:", error);
        setError("Error al cargar el reproductor de video");
      }
    }
  }, [videoUrl, thumbnailUrl, loadingVideo, idCapitulo, viewIncremented]);


  // Guardar progreso antes de salir
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (capitulo && totalWatchedTime > 0 && videoDuration > 0) {
        const progressPercentage = (totalWatchedTime / videoDuration) * 100;
        // Aquí enviar el progreso al backend si se implementa esa funcionalidad
        // saveVideoProgress(capitulo.videoShowId, totalWatchedTime, progressPercentage);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [capitulo, totalWatchedTime, videoDuration]);

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-gray-900 to-black text-white min-h-screen flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-xl font-semibold">Cargando información del video...</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-gradient-to-b from-gray-900 to-black text-white min-h-screen flex items-center justify-center p-8">
        <div className="bg-red-900/50 p-6 rounded-lg max-w-md text-center backdrop-blur-sm border border-red-700">
          <h2 className="text-2xl font-bold mb-4">¡Algo salió mal!</h2>
          <p className="text-lg mb-4">{error}</p>
          <div className="flex flex-col space-y-2">
            <button 
              onClick={() => window.location.reload()} 
              className="px-6 py-2 bg-purple-700 hover:bg-purple-600 rounded-full transition-all duration-300"
            >
              Reintentar
            </button>
            <Link 
              to="/" 
              className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-full transition-all duration-300"
            >
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!capitulo) {
    return null;
  }

  // Preparar subtítulos (solo si existen y son accesibles)
  const subtitulos = capitulo.subtitulos || [];
  const renderSubtitulos = () => {
    try {
      return subtitulos.map((subtitle) => (
        <track 
          key={subtitle.subtituloId || Math.random().toString()}
          kind="subtitles" 
          label={subtitle.idioma}
          srcLang={subtitle.idioma?.toLowerCase() || 'es'} 
          src={URL.createObjectURL(new Blob([atob(subtitle.subtituloBase64)], { type: 'text/vtt' }))}
        />
      ));
    } catch (e) {
      console.error("Error al procesar subtítulos:", e);
      return null;
    }
  };

  const adjustColor = (hex, amount) => {
    hex = (hex || '').replace('#', '');
    if (hex.length !== 6) return '#000000';
    let r = Math.max(0, Math.min(255, parseInt(hex.slice(0, 2), 16) + amount));
    let g = Math.max(0, Math.min(255, parseInt(hex.slice(2, 4), 16) + amount));
    let b = Math.max(0, Math.min(255, parseInt(hex.slice(4, 6), 16) + amount));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  return (
    <div className="bg-gradient-to-b from-gray-900 to-black text-white min-h-screen pt-2 sm:pt-4">
      {/* Contenedor principal */}
      <div className="max-w-[1700px] mx-auto px-2 sm:px-4 lg:px-6">
        {/* Grid principal */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Columna principal (video y detalles) */}
          <div className="lg:col-span-9">
            {/* Reproductor de video */}
            <div className="video-container w-full aspect-video shadow-2xl mb-4 rounded-lg sm:rounded-xl overflow-hidden" style={{ userSelect: 'none' }}>
              {loadingVideo ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-4 text-sm">Cargando video...</p>
                  </div>
                </div>
              ) : (
                <video id="video-id" ref={videoRef} className="w-full h-full">
                  {videoUrl && <source src={videoUrl} type="video/mp4" />}
                  {subtitulos.length > 0 && renderSubtitulos()}
                  Tu navegador no soporta la reproducción de video.
                </video>
              )}
            </div>
           
            {/* Información del video */}
            <div className="mb-6 px-2 sm:px-4">
              {/* Título */}
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-3">
                {capitulo.titulo || 'Sin título'}
              </h1>
             
              {/* Canal, visualizaciones y acciones */}
              <div className="flex flex-wrap items-start sm:items-center justify-between mb-4">
                <div className="flex items-center mb-3 sm:mb-0">
                  {/* Avatar y nombre de comediante */}
                  {capitulo.comediantes && capitulo.comediantes.length > 0 && capitulo.comediantes[0] && (
                    <div 
                      className="flex items-center cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => handleComedianteClick(
                        typeof capitulo.comediantes[0] === 'object' ? capitulo.comediantes[0].comedianteId : null
                      )}
                    >
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-600 flex items-center justify-center mr-2 overflow-hidden flex-shrink-0">
                        {typeof capitulo.comediantes[0] === 'object' && capitulo.comediantes[0].imagen ? (
                          <img
                            src={
                              capitulo.comediantes[0].imagen && capitulo.comediantes[0].imagen.trim() !== ''
                                ? capitulo.comediantes[0].imagen
                                : sinFoto
                            }
                            alt={capitulo.comediantes[0].nombre}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = sinFoto;
                            }}
                          />
                        ) : (
                          <span className="text-lg sm:text-xl font-bold">
                            {typeof capitulo.comediantes[0] === 'object' 
                              ? capitulo.comediantes[0].nombre.substring(0, 1) 
                              : (typeof capitulo.comediantes[0] === 'string' 
                                ? capitulo.comediantes[0].substring(0, 1) 
                                : 'C')}
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="font-semibold text-white text-sm sm:text-base hover:underline">
                          {typeof capitulo.comediantes[0] === 'object' 
                            ? capitulo.comediantes[0].nombre 
                            : (typeof capitulo.comediantes[0] === 'string' 
                              ? capitulo.comediantes[0] 
                              : 'Comediante')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
               
                {/* Botones de acción */}
                <div className="flex flex-wrap sm:flex-nowrap gap-2">
                  <div className="flex items-center rounded-full bg-gray-800 overflow-hidden">
                    <button 
                      onClick={handleLike}
                      disabled={ratingLoading}
                      className={`flex items-center px-2 py-1 sm:px-3 sm:py-1.5 transition-colors ${
                        userRating === true 
                          ? 'bg-purple-600 hover:bg-purple-700' 
                          : 'hover:bg-gray-700'
                      } ${ratingLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <FaThumbsUp className="mr-1 sm:mr-2" />
                      <span className="text-sm">{totalLikes}</span>
                    </button>
                    <div className="h-5 w-px bg-gray-700"></div>
                    <button 
                      onClick={handleDislike}
                      disabled={ratingLoading}
                      className={`px-2 py-1 sm:px-3 sm:py-1.5 transition-colors ${
                        userRating === false 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : 'hover:bg-gray-700'
                      } ${ratingLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <FaThumbsDown />
                    </button>
                  </div>
                 
                  {/* Botón de compartir */}
                  <button 
                    className="bg-gray-800 hover:bg-gray-700 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full flex items-center text-sm relative transition-colors"
                    onClick={handleShare}
                  >
                    {copySuccess ? (
                      <>
                        <FaCheck className="mr-1 sm:mr-2 text-green-400" />
                        <span>Copiado</span>
                      </>
                    ) : (
                      <>
                        <FaShare className="mr-1 sm:mr-2" />
                        <span>Compartir</span>
                      </>
                    )}
                  </button>
                 
                  {/* Botón de más opciones */}
                  <button className="bg-gray-800 hover:bg-gray-700 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full flex items-center text-sm transition-colors">
                    <FaEllipsisH />
                  </button>
                </div>
              </div>
             
              {/* Visualizaciones y fecha */}
              <div className="bg-gray-800/60 rounded-lg p-3 mb-4">
                <div className="flex flex-wrap items-center text-xs sm:text-sm text-gray-300 mb-3">
                  <div className="flex items-center mr-4">
                    <FaEye className="mr-1 text-purple-400" size={12} />
                    <span>{capitulo.vistas || 0} visualizaciones</span>
                  </div>
                  <span className="hidden xs:inline mx-2">•</span>
                  <div className="flex items-center mt-1 xs:mt-0">
                    <FaRegCalendarAlt className="mr-1 text-purple-400" size={12} />
                    <span>
                      {capitulo.fechaEstreno ? 
                        `Publicado el ${new Date(capitulo.fechaEstreno).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}` : 
                        'Fecha desconocida'}
                    </span>
                  </div>
                  {capitulo.isPremium && (
                    <>
                      <span className="hidden xs:inline mx-2">•</span>
                      <div className="flex items-center mt-1 xs:mt-0">
                        <span className="bg-yellow-600 text-yellow-100 text-xs px-2 py-0.5 rounded-full font-medium">
                          Premium
                        </span>
                      </div>
                    </>
                  )}
                </div>
               
                {/* Descripción */}
                <p className="text-gray-200 text-xs sm:text-sm leading-relaxed whitespace-pre-line">
                  {capitulo.descripcion || 'Sin descripción'}
                </p>
               
                {/* Tags */}
                {capitulo.tags && capitulo.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 sm:gap-2 mt-3">
                    {capitulo.tags.map((tag, index) => {
                      const tagName = tag.nombre || tag;
                      const tagColor = tag.colorHex || '#EF4444';

                      const darkerBorder = adjustColor(tagColor, -30);
                      const lighterText = adjustColor(tagColor, 100);

                      return (
                        <div
                          key={tag.tagId || `tag-${index}`}
                          className="px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full text-[10px] sm:text-xs font-medium transition-transform hover:scale-105"
                          style={{
                            backgroundColor: tagColor + '40', // con transparencia
                            borderColor: darkerBorder,
                            color: lighterText,
                            border: '1px solid',
                            textShadow: '0 0 2px rgba(0,0,0,0.5)',
                          }}
                        >
                          #{tagName}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
             
              {/* Información adicional */}
              {capitulo.comediantes && capitulo.comediantes.length > 1 && (
                <div className="bg-gray-800/60 rounded-lg p-3 mb-4">
                  <h3 className="text-sm font-semibold text-white mb-2">Otros comediantes</h3>
                  <div className="flex flex-wrap gap-2">
                    {capitulo.comediantes.slice(1).map((comediante, index) => (
                      <button
                        key={typeof comediante === 'object' ? comediante.comedianteId : index}
                        onClick={() => handleComedianteClick(
                          typeof comediante === 'object' ? comediante.comedianteId : null
                        )}
                        className="flex items-center bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded-full text-xs transition-colors"
                      >
                        <div className="w-4 h-4 rounded-full bg-purple-600 flex items-center justify-center mr-1 text-[8px] font-bold">
                          {typeof comediante === 'object' 
                            ? comediante.nombre.substring(0, 1) 
                            : comediante.substring(0, 1)}
                        </div>
                        <span>
                          {typeof comediante === 'object' ? comediante.nombre : comediante}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
         
          {/* Columna de sugerencias - Desktop */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="sticky top-4">
              <SugeridosDestacados currentVideoId={idCapitulo} />
            </div>
          </div>
         
          {/* Videos sugeridos para móviles */}
          <div className="lg:hidden px-2">
            <div className="border-t border-gray-700 pt-4">
              <h2 className="text-lg font-bold text-white mb-4">Videos relacionados</h2>
              <SugeridosDestacados currentVideoId={idCapitulo} isMobile={true} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReproductorVideoShows;